import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.overview');
import { OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

interface SamplePoint { timestamp: string; I3: number; ASC: number; vehicle: number; }
interface SampleAgg {
    timestamps: string[];
    I3: { mean: number; std: number; min: number; max: number; cv: number; n: number; range: number };
    ASC: any; vehicle: any;
    points: SamplePoint[];
}

@Component({
    selector: 'wiz-page-overview',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.overview/view.scss */
:host {
  display: block;
  height: 100%;
}`],
})
export class PageOverviewComponent implements OnInit {
    public loading = true;
    public totals: any = {};
    public samples: string[] = [];
    public perSample: { [k: string]: SampleAgg } = {};
    public sampleList: { name: string; short: string; data: SampleAgg }[] = [];

    // shared chart bounds
    public i3Min = 0;
    public i3Max = 200;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        await this.load();
    }

    public async load() {
        this.loading = true;
        try {
            const { code, data } = await wiz.call("overview");
            if (code === 401) {
                location.href = "/access";
                return;
            }
            if (code === 200 && data) {
                this.totals = data.totals || {};
                this.samples = data.samples || [];
                this.perSample = data.per_sample || {};

                this.sampleList = this.samples
                    .map(s => ({
                        name: s,
                        short: this.shortName(s),
                        data: this.perSample[s],
                    }))
                    .filter((sample): sample is { name: string; short: string; data: SampleAgg } => !!sample.data?.I3);

                // Global range across all points for chart scale
                const all = this.sampleList.flatMap(sample =>
                    (sample.data.points || [])
                        .filter(point => point.I3 != null)
                        .map(point => point.I3)
                );

                if (all.length > 0) {
                    const lo = Math.min(...all);
                    const hi = Math.max(...all);
                    const pad = (hi - lo) * 0.1 || 10;
                    this.i3Min = Math.floor(lo - pad);
                    this.i3Max = Math.ceil(hi + pad);
                } else {
                    this.i3Min = 0;
                    this.i3Max = 100;
                }
            }
        } finally {
            this.loading = false;
            await this.service.render();
        }
    }

    public shortName(name: string): string {
        const m = name.match(/26AN\d+/);
        return m ? m[0] : name;
    }

    public sampleSuffix(name: string, shortName?: string): string {
        const code = shortName || this.shortName(name);
        return name.replace(code, '').trim();
    }

    public fmt(v: any, d = 1): string {
        return v == null ? '-' : Number(v).toFixed(d);
    }

    public cvClass(cv: number): string {
        if (cv == null) return 'text-gray-900';
        if (cv > 12) return 'text-red-600';
        if (cv > 7) return 'text-amber-600';
        return 'text-emerald-600';
    }

    public cvBadge(cv: number): string {
        if (cv == null) return 'ring-gray-300 text-gray-600';
        if (cv > 12) return 'ring-red-300 text-red-700 bg-red-50';
        if (cv > 7) return 'ring-amber-300 text-amber-700 bg-amber-50';
        return 'ring-emerald-300 text-emerald-700 bg-emerald-50';
    }

    public pointLeft(val: number): string {
        const range = this.i3Max - this.i3Min || 1;
        const pct = ((val - this.i3Min) / range) * 100;
        return Math.max(0, Math.min(100, pct)).toFixed(1) + '%';
    }

    public rangeBarStyle(data: SampleAgg): { left: string; width: string } {
        if (!data?.I3) return { left: '0%', width: '0%' };
        const range = this.i3Max - this.i3Min || 1;
        const left = ((data.I3.min - this.i3Min) / range) * 100;
        const right = ((data.I3.max - this.i3Min) / range) * 100;
        return {
            left: Math.max(0, left).toFixed(1) + '%',
            width: Math.max(0.5, right - left).toFixed(1) + '%',
        };
    }

    public goSample(name: string) {
        const short = this.shortName(name);
        this.service.href('/samples/' + encodeURIComponent(short));
    }
}

export default PageOverviewComponent;